import {
  IImageStorage,
  STORED_IMAGE_PREFIX
} from '../../data_sources/imagestorage/imageStorage'
import {
  ChecklistPhotoList,
  IChecklist,
  IPhoto,
  IPhotoComment
} from '../../models/Checklist'
import { formatDate } from '../dates'

export function collateNewChecklistPhotoComments(
  checklistPhotos: ChecklistPhotoList,
  checklistData: IChecklist
): void {
  const newComments: IPhotoComment[] = []

  checklistPhotos.forEach((photo) => {
    if (photo.photo.comments) {
      photo.photo.comments
        .filter((c) => c.action != null)
        .forEach((c1) => newComments.push(c1))
    }
  })
  checklistData.newPhotoComments = newComments
}

export function collateChecklistPhotos(
  checklistData: IChecklist
): ChecklistPhotoList {
  const photoList: ChecklistPhotoList = []

  checklistData.groups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.photos) {
        field.photos.forEach((photo) => {
          photoList.push({
            fieldId: field.id.toString(),
            groupId: '',
            photo
          })
        })
        field.photos = null // API does not read photos from here
      }
      if (
        field.dataGrid &&
        field.dataGrid.values &&
        field.dataGrid.values.length
      ) {
        field.dataGrid.values.forEach((row, rowIdx) => {
          row.forEach((gridField, colIdx) => {
            if (gridField.photos) {
              gridField.photos.forEach((photo) => {
                photoList.push({
                  fieldId: field.id.toString(),
                  groupId: '',
                  row: rowIdx + 1,
                  col: colIdx + (field.type == 'fixeddatagrid' ? 2 : 1),
                  photo
                })
              })
              gridField.photos = null // API does not read photos from here
            }
          })
        })
      }
    })
    if (group.photos) {
      group.photos.forEach((photo) => {
        let photoGroupID = null
        if (group.checklistGroupId != null) {
          photoGroupID = group.checklistGroupId.toString()
        } else {
          photoGroupID = group.id.toString()
        }
        photoList.push({
          fieldId: '',
          groupId: photoGroupID,
          photo
        })
      })
      group.photos = null // API does not read photos from here
    }
  })
  return photoList
}

export function fixChecklistData(checklistData: IChecklist): void {
  // Delete fields that are not accepted by backend
  delete checklistData.editEmail
  // Set the useAttributes field correctly
  if (checklistData.template) {
    checklistData.useAttributes = true
  }
  checklistData.groups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.dateTimeIncludeOffset) {
        delete field.dateTimeIncludeOffset
      }
      if (field.dateFormat) {
        delete field.dateFormat
      }
      if (field.dateTimeFormat) {
        delete field.dateTimeFormat
      }
      if (
        field.dataGrid &&
        field.dataGrid.values &&
        field.dataGrid.values.length
      ) {
        field.dataGrid.values.forEach((row, _rowIdx) => {
          row.forEach((gridField, _colIdx) => {
            if (gridField.dateTimeIncludeOffset) {
              delete gridField.dateTimeIncludeOffset
            }
            if (gridField.dateFormat) {
              delete gridField.dateFormat
            }
            if (gridField.dateTimeFormat) {
              delete gridField.dateTimeFormat
            }
            if (gridField.conditions) {
              delete gridField.conditions
            }
            if (gridField.conditionalRule) {
              delete gridField.conditionalRule
            }
          })
        })
        if (field.dataGrid.columnLabels) {
          field.dataGrid.columnLabels.forEach((columnLabel, _i) => {
            if (columnLabel.field && columnLabel.field.conditions) {
              delete columnLabel.field.conditions
            }
            if (columnLabel.field && columnLabel.field.conditionalRule) {
              delete columnLabel.field.conditionalRule
            }
          })
        }
      }
      // conditional questions, we don't need to serialize conditional rules
      if (field.conditions) {
        delete field.conditions
      }
      if (field.conditionalRule) {
        delete field.conditionalRule
      }
    })
  })
}

export async function addChecklistPhotosToFormData(
  imageStorage: IImageStorage,
  photos: ChecklistPhotoList,
  data: FormData,
  attachmentField = 'attachment[]',
  attachmentTemp = 'attachmentTemp[]'
): Promise<void> {
  const isAttachmentLike = (t: IPhoto['attachmentType']) =>
    t === 'attachment' || t === 'video'
  // Get photo data
  for (const photo of photos) {
    // Make sure we don't try to upload images already on server
    console.log('checking photo', photo)
    if (
      !isAttachmentLike(photo.photo.attachmentType) &&
      photo.photo.url.startsWith(STORED_IMAGE_PREFIX)
    ) {
      console.log('Sending...')
      const photoData = await imageStorage.getImageData(photo.photo.url)
      const meta = photo.photo
      let takenDate = meta.photoTakenDate || ''
      if (takenDate) {
        takenDate = formatDate('exif', takenDate)
      }
      data.append(attachmentField, photoData, meta.fileName)
      data.append(meta.fileName, meta.comment || '')
      data.append(meta.fileName + 'photoTakenDate', takenDate)
      data.append(meta.fileName + 'fieldID', photo.fieldId)
      data.append(meta.fileName + 'groupID', photo.groupId)
      if (typeof photo.row != 'undefined' && typeof photo.col != 'undefined') {
        data.append(meta.fileName + 'row', String(photo.row))
        data.append(meta.fileName + 'col', String(photo.col))
      }
    } else if (
      isAttachmentLike(photo.photo.attachmentType) &&
      photo.photo.uuid
    ) {
      data.append(attachmentTemp, photo.photo.uuid)
      data.append(photo.photo.uuid, photo.photo.comment || '')
      data.append(
        photo.photo.uuid + 'attachmentType',
        photo.photo.attachmentType || 'attachment'
      )
    } else if (
      isAttachmentLike(photo.photo.attachmentType) &&
      photo.photo.file
    ) {
      // New video/file attachment not yet uploaded (has file blob but no uuid)
      const meta = photo.photo
      data.append(attachmentField, photo.photo.file, meta.fileName)
      data.append(meta.fileName, meta.comment || '')
      data.append(meta.fileName + 'fieldID', photo.fieldId)
      data.append(meta.fileName + 'groupID', photo.groupId)
      data.append(
        meta.fileName + 'attachmentType',
        meta.attachmentType || 'attachment'
      )
      if (typeof photo.row != 'undefined' && typeof photo.col != 'undefined') {
        data.append(meta.fileName + 'row', String(photo.row))
        data.append(meta.fileName + 'col', String(photo.col))
      }
    }
  }
}
